/**
 * Insights pédagogiques élève — source de vérité partagée (Suivi).
 * Fonctions pures, testables, sans dépendance UI.
 */

import { DEGREE_TO_FUNCTIONS } from './riemannFunctions'
import {
  revalidateStoredMarkerAnswer,
  isPartialValidationLevel
} from './exerciseMarkerValidation'
import { calculateStreak } from './dailyStreak'
import {
  detectErrorPatterns,
  patternsToWeakPoints,
  getRecentAttempts
} from './errorPatternInsights'

/** Cadence stockée (stats) → tag autoTags Mode Libre */
const CADENCE_TO_FILTER_TAG = {
  perfect: 'CadenceParfaite',
  imperfect: 'CadenceImparfaite',
  plagal: 'CadencePlagale',
  deceptive: 'CadenceRompue',
  half: 'DemiCadence',
  'demi-cadence': 'DemiCadence'
}

/** Degré rare / sans exo tagué → fonction Riemann (tag catalogue) */
const DEGREE_TO_FUNCTION_FILTER_TAG = {
  I: 'Tonique',
  III: 'Tonique',
  VI: 'Tonique',
  IV: 'SousDominante',
  II: 'SousDominante',
  V: 'Dominante',
  VII: 'Dominante'
}

export const CADENCE_LABELS = {
  perfect: 'Cadence Parfaite',
  imperfect: 'Cadence Imparfaite',
  plagal: 'Cadence Plagale',
  deceptive: 'Cadence Rompue',
  half: 'Demi-Cadence'
}

const CADENCE_SHORT_LABELS = {
  perfect: 'Parfaite',
  parfaite: 'Parfaite',
  half: 'Demi',
  demi: 'Demi',
  'demi-cadence': 'Demi',
  plagal: 'Plagale',
  plagale: 'Plagale',
  imperfect: 'Imparfaite',
  deceptive: 'Rompue',
  sans: 'Sans cadence',
  '': 'Sans cadence'
}

/**
 * Registre compétence / cadence / degré → fiche Codex.
 * Enrichir au fil de la construction du Codex ; les motifs d'erreur
 * portent aussi leur lien inline dans errorPatternInsights.js.
 */
export const WEAK_SKILL_CODEX = {
  perfect: { ficheId: 'fiche-demi-cadence-parfaite', sectionId: 'cadence-parfaite' },
  half: { ficheId: 'fiche-demi-cadence-parfaite', sectionId: 'demi-cadence' },
  'demi-cadence': { ficheId: 'fiche-demi-cadence-parfaite', sectionId: 'demi-cadence' },
  CadenceParfaite: { ficheId: 'fiche-demi-cadence-parfaite', sectionId: 'cadence-parfaite' },
  CadenceImparfaite: { ficheId: 'fiche-cadence-imparfaite', sectionId: null },
  CadencePlagale: { ficheId: 'fiche-cadence-plagale', sectionId: null },
  CadenceRompue: { ficheId: 'fiche-cadence-rompue', sectionId: null },
  DemiCadence: { ficheId: 'fiche-demi-cadence-parfaite', sectionId: 'demi-cadence' },
  plagal: { ficheId: 'fiche-cadence-plagale', sectionId: null },
  deceptive: { ficheId: 'fiche-cadence-rompue', sectionId: null },
  imperfect: { ficheId: 'fiche-cadence-imparfaite', sectionId: null },
  I: { ficheId: 'fiche-tonique', sectionId: null },
  V: { ficheId: 'fiche-dominante', sectionId: null },
  IV: { ficheId: 'fiche-sous-dominante', sectionId: null },
  II: { ficheId: 'fiche-sous-dominante', sectionId: null },
  VI: { ficheId: 'fiche-tonique', sectionId: null },
  III: { ficheId: 'fiche-tonique', sectionId: null },
  VII: { ficheId: 'fiche-dominante', sectionId: null },
  Tonique: { ficheId: 'fiche-tonique', sectionId: null },
  SousDominante: { ficheId: 'fiche-sous-dominante', sectionId: null },
  Dominante: { ficheId: 'fiche-dominante', sectionId: null },
  PremierRenversement: { ficheId: 'fiche-renversements', sectionId: null },
  SecondRenversement: { ficheId: 'fiche-renversements', sectionId: null }
}

function attemptDate(attempt) {
  if (!attempt?.completedAt) return null
  return attempt.completedAt.toDate ? attempt.completedAt.toDate() : new Date(attempt.completedAt)
}

function attemptTime(attempt) {
  const date = attemptDate(attempt)
  return date ? date.getTime() : 0
}

/**
 * Meilleure tentative par exerciseId (record personnel).
 * Égalité de score → la plus récente. Sans exerciseId → chaque tentative compte seule.
 */
export function getBestAttemptsByExercise(attempts) {
  const best = new Map()
  const orphans = []

  for (const attempt of attempts || []) {
    const exerciseId = attempt?.exerciseId
    if (!exerciseId) {
      orphans.push(attempt)
      continue
    }
    const prev = best.get(exerciseId)
    const score = attempt.score ?? 0
    if (!prev) {
      best.set(exerciseId, attempt)
      continue
    }
    const prevScore = prev.score ?? 0
    if (score > prevScore || (score === prevScore && attemptTime(attempt) > attemptTime(prev))) {
      best.set(exerciseId, attempt)
    }
  }

  return [...best.values(), ...orphans]
}

/** Nombre d'exercices distincts complétés (exerciseId unique). */
export function countUniqueExercises(attempts) {
  const ids = new Set()
  let orphans = 0
  for (const attempt of attempts || []) {
    if (attempt?.exerciseId) ids.add(attempt.exerciseId)
    else orphans += 1
  }
  return ids.size + orphans
}

/**
 * Groupe les tentatives par exerciseId pour le journal (tri : dernière activité desc).
 */
export function groupAttemptsByExercise(attempts) {
  const groups = new Map()
  const orphans = []

  for (const attempt of attempts || []) {
    const exerciseId = attempt?.exerciseId
    if (!exerciseId) {
      orphans.push({
        exerciseId: null,
        exerciseTitle: attempt.exerciseTitle || 'Exercice',
        bestScore: attempt.score ?? 0,
        attemptCount: 1,
        lastCompletedAt: attempt.completedAt,
        attempts: [attempt],
        bestAttempt: attempt
      })
      continue
    }
    if (!groups.has(exerciseId)) {
      groups.set(exerciseId, {
        exerciseId,
        exerciseTitle: attempt.exerciseTitle || 'Exercice',
        bestScore: attempt.score ?? 0,
        attemptCount: 0,
        lastCompletedAt: attempt.completedAt,
        attempts: [],
        bestAttempt: attempt
      })
    }
    const group = groups.get(exerciseId)
    group.attemptCount += 1
    group.attempts.push(attempt)
    const score = attempt.score ?? 0
    const bestScore = group.bestScore ?? 0
    if (
      score > bestScore ||
      (score === bestScore && attemptTime(attempt) > attemptTime(group.bestAttempt))
    ) {
      group.bestScore = score
      group.bestAttempt = attempt
    }
    if (attemptTime(attempt) >= attemptTime({ completedAt: group.lastCompletedAt })) {
      group.lastCompletedAt = attempt.completedAt
      group.exerciseTitle = attempt.exerciseTitle || group.exerciseTitle
    }
  }

  const sorted = [...groups.values()].sort(
    (a, b) => attemptTime({ completedAt: b.lastCompletedAt }) - attemptTime({ completedAt: a.lastCompletedAt })
  )
  return [...sorted, ...orphans.sort((a, b) => attemptTime(b) - attemptTime(a))]
}

function calculateChange(current, previous) {
  if (previous === 0) {
    return { direction: current > 0 ? 'up' : 'stable', percentage: 0 }
  }
  const change = ((current - previous) / previous) * 100
  return {
    direction: change > 0 ? 'up' : change < 0 ? 'down' : 'stable',
    percentage: Math.round(Math.abs(change) * 10) / 10
  }
}

export function getThisWeekAttempts(attempts) {
  const weekAgo = new Date()
  weekAgo.setDate(weekAgo.getDate() - 7)
  return (attempts || []).filter((a) => {
    const date = attemptDate(a)
    return date && date >= weekAgo
  })
}

export function getLastWeekAttempts(attempts) {
  const now = new Date()
  const weekAgo = new Date(now)
  weekAgo.setDate(weekAgo.getDate() - 7)
  const twoWeeksAgo = new Date(now)
  twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14)
  return (attempts || []).filter((a) => {
    const date = attemptDate(a)
    return date && date >= twoWeeksAgo && date < weekAgo
  })
}

/**
 * Stats par degré / cadence / emprunts + totaux globaux.
 * Performance (moyenne, degrés/cadences) : meilleure tentative par exercice.
 * totalSessions : toutes les complétions ; totalAttempts : exercices uniques.
 */
export function buildDegreeAndCadenceStats(attempts) {
  const list = attempts || []
  const totalSessions = list.length
  const totalAttempts = countUniqueExercises(list)
  const performanceAttempts = getBestAttemptsByExercise(list)
  const totalQuestions = performanceAttempts.reduce((sum, a) => sum + (a.totalQuestions || 0), 0)
  const totalCorrect = performanceAttempts.reduce((sum, a) => sum + (a.correctCount || 0), 0)
  const averageScore =
    performanceAttempts.length > 0
      ? performanceAttempts.reduce((sum, a) => sum + (a.score || 0), 0) / performanceAttempts.length
      : 0

  const degreeStats = {}
  const cadenceStats = {}
  const borrowedStats = { total: 0, correct: 0 }

  performanceAttempts.forEach((attempt) => {
    if (!attempt.correctAnswers || !Array.isArray(attempt.correctAnswers)) return
    attempt.correctAnswers.forEach((correct, index) => {
      if (!correct) return
      const userAnswer = attempt.userAnswers?.[index]
      const validation = revalidateStoredMarkerAnswer(userAnswer, correct, {
        index,
        progressionMode: attempt.progressionMode,
        tonality: attempt.tonality,
        mode: attempt.mode || attempt.dominantMode
      })

      const isCorrect = validation.level === 1
      const isPartial = isPartialValidationLevel(validation.level)
      const isIncorrect = !isCorrect && !isPartial
      const score = validation.score || 0

      const degree = correct.degree || ''
      if (degree) {
        if (!degreeStats[degree]) {
          degreeStats[degree] = {
            total: 0,
            correct: 0,
            partial: 0,
            incorrect: 0,
            totalScore: 0,
            averageScore: 0,
            byFigure: {}
          }
        }
        degreeStats[degree].total++
        if (isCorrect) degreeStats[degree].correct++
        else if (isPartial) degreeStats[degree].partial++
        else if (isIncorrect) degreeStats[degree].incorrect++
        degreeStats[degree].totalScore += score
        degreeStats[degree].averageScore = Math.round(
          degreeStats[degree].totalScore / degreeStats[degree].total
        )

        const figureKey = correct.figure && correct.figure !== '5' ? correct.figure : ''
        const byFigure = degreeStats[degree].byFigure
        if (!byFigure[figureKey]) {
          byFigure[figureKey] = {
            total: 0,
            correct: 0,
            partial: 0,
            incorrect: 0,
            totalScore: 0,
            averageScore: 0
          }
        }
        byFigure[figureKey].total++
        if (isCorrect) byFigure[figureKey].correct++
        else if (isPartial) byFigure[figureKey].partial++
        else if (isIncorrect) byFigure[figureKey].incorrect++
        byFigure[figureKey].totalScore += score
        byFigure[figureKey].averageScore = Math.round(
          byFigure[figureKey].totalScore / byFigure[figureKey].total
        )
      }

      const cadence = correct.cadence || ''
      if (cadence) {
        if (!cadenceStats[cadence]) {
          cadenceStats[cadence] = {
            total: 0,
            correct: 0,
            partial: 0,
            incorrect: 0,
            totalScore: 0,
            averageScore: 0
          }
        }
        cadenceStats[cadence].total++
        if (isCorrect) cadenceStats[cadence].correct++
        else if (isPartial) cadenceStats[cadence].partial++
        else if (isIncorrect) cadenceStats[cadence].incorrect++
        cadenceStats[cadence].totalScore += score
        cadenceStats[cadence].averageScore = Math.round(
          cadenceStats[cadence].totalScore / cadenceStats[cadence].total
        )
      }

      if (correct.isBorrowed === true) {
        borrowedStats.total++
        if (isCorrect) borrowedStats.correct++
      }
    })
  })

  return {
    totalAttempts,
    totalSessions,
    totalQuestions,
    totalCorrect,
    averageScore,
    degreeStats,
    cadenceStats,
    borrowedStats,
    streak: calculateStreak(list)
  }
}

/**
 * Point fort / point faible (minAttempts réponses).
 * Si `availableTags` est fourni, le point faible CTA est restreint aux skills
 * dont un tag d’exercice existe réellement dans le catalogue (sinon null).
 */
export function getStrongestAndWeakest(profileStats, { minAttempts = 3, availableTags = null } = {}) {
  if (!profileStats) return { strongestPoint: null, weakestPoint: null }

  const { degreeStats = {}, cadenceStats = {} } = profileStats
  const allStats = []

  Object.entries(degreeStats).forEach(([degree, stats]) => {
    const percentage =
      stats.averageScore != null
        ? Math.round(stats.averageScore)
        : stats.total > 0
          ? Math.round((stats.correct / stats.total) * 100)
          : 0
    allStats.push({
      name: degree,
      percentage,
      total: stats.total,
      isDegree: true,
      type: 'degree',
      value: degree
    })
  })

  Object.entries(cadenceStats).forEach(([cadence, stats]) => {
    const percentage =
      stats.averageScore != null
        ? Math.round(stats.averageScore)
        : stats.total > 0
          ? Math.round((stats.correct / stats.total) * 100)
          : 0
    allStats.push({
      name: CADENCE_LABELS[cadence] || cadence,
      percentage,
      total: stats.total,
      isDegree: false,
      type: 'cadence',
      value: cadence
    })
  })

  const validStats = allStats.filter((s) => s.total >= minAttempts)
  if (validStats.length === 0) return { strongestPoint: null, weakestPoint: null }

  const sorted = [...validStats].sort((a, b) => b.percentage - a.percentage)
  const strongestPoint = sorted[0]
  let weakestPoint = sorted[sorted.length - 1]

  if (availableTags != null) {
    const tagSet = toTagSet(availableTags)
    const actionableAsc = [...sorted]
      .reverse()
      .map((s) => {
        const filterTag = resolveActionableFilterTag(s, tagSet)
        if (!filterTag) return null
        return {
          ...s,
          filterTag,
          practiceLabel: formatPracticeLabel(filterTag, s)
        }
      })
      .filter(Boolean)
    weakestPoint = actionableAsc[0] || null
  } else if (weakestPoint) {
    weakestPoint = {
      ...weakestPoint,
      filterTag: getFilterTagForWeakSkill(weakestPoint)
    }
  }

  return { strongestPoint, weakestPoint }
}

function cadenceStatPercentage(stats) {
  return stats.averageScore != null
    ? Math.round(stats.averageScore)
    : stats.total > 0
      ? Math.round((stats.correct / stats.total) * 100)
      : 0
}

/**
 * Meilleure / plus faible cadence à partir des stats de profil (même base que getStrongestAndWeakest).
 */
export function getCadenceHighlightsFromStats(profileStats, { minAttempts = 3 } = {}) {
  const cadenceStats = profileStats?.cadenceStats || {}
  const entries = Object.entries(cadenceStats)
    .filter(([, stats]) => stats.total >= minAttempts)
    .map(([cadence, stats]) => ({
      name: CADENCE_LABELS[cadence] || cadence,
      pct: cadenceStatPercentage(stats),
      total: stats.total
    }))
    .sort((a, b) => b.pct - a.pct)

  const total = Object.values(cadenceStats).reduce((sum, stats) => sum + stats.total, 0)

  return {
    total,
    best: entries[0] ? { name: entries[0].name, pct: entries[0].pct } : null,
    weak:
      entries.length > 1
        ? { name: entries[entries.length - 1].name, pct: entries[entries.length - 1].pct }
        : null
  }
}

/**
 * Compétences faibles pour suggestions / plan de pratique.
 * Avec `availableTags`, ne garde que les skills actionnables via un tag d’exercice présent.
 */
export function getWeakSkills(profileStats, { threshold = 70, minAttempts = 3, availableTags = null } = {}) {
  if (!profileStats) return []
  const weakPoints = []

  if (profileStats.degreeStats) {
    Object.entries(profileStats.degreeStats).forEach(([degree, stat]) => {
      if (stat.total >= minAttempts) {
        const successRate =
          stat.averageScore != null
            ? Math.round(stat.averageScore)
            : (stat.correct / stat.total) * 100
        if (successRate < threshold) {
          weakPoints.push({
            type: 'degree',
            value: degree,
            successRate,
            label: degree,
            filterTag: getFilterTagForWeakSkill({ type: 'degree', value: degree }),
            codex: WEAK_SKILL_CODEX[degree] || null
          })
        }
      }
    })
  }

  if (profileStats.cadenceStats) {
    Object.entries(profileStats.cadenceStats).forEach(([cadence, stat]) => {
      if (stat.total >= minAttempts) {
        const successRate =
          stat.averageScore != null
            ? Math.round(stat.averageScore)
            : (stat.correct / stat.total) * 100
        if (successRate < threshold) {
          weakPoints.push({
            type: 'cadence',
            value: cadence,
            successRate,
            label: CADENCE_LABELS[cadence] || cadence,
            filterTag: getFilterTagForWeakSkill({ type: 'cadence', value: cadence }),
            codex: WEAK_SKILL_CODEX[cadence] || null
          })
        }
      }
    })
  }

  const sorted = weakPoints.sort((a, b) => a.successRate - b.successRate)
  if (availableTags == null) return sorted

  const tagSet = toTagSet(availableTags)
  return sorted
    .map((w) => {
      const filterTag = resolveActionableFilterTag(w, tagSet)
      if (!filterTag) return null
      return {
        ...w,
        filterTag,
        practiceLabel: formatPracticeLabel(filterTag, w)
      }
    })
    .filter(Boolean)
}

/**
 * Snapshot pédagogique (fonctions, cadences, degrés, position) sur les N derniers exercices.
 */
export function getPedagogicalSnapshot(attempts, limit = 50) {
  const sorted = [...(attempts || [])].sort((a, b) => {
    const dateA = attemptDate(a)?.getTime() || 0
    const dateB = attemptDate(b)?.getTime() || 0
    return dateB - dateA
  })
  const slice = sorted.slice(0, limit)

  const byFunction = {
    T: { correct: 0, total: 0 },
    SD: { correct: 0, total: 0 },
    D: { correct: 0, total: 0 },
    total: 0
  }
  const byCadence = {}
  const byDegree = {}
  const byFigure = {
    fundamental: { correct: 0, total: 0 },
    renversement: { correct: 0, total: 0 },
    total: 0
  }

  slice.forEach((attempt) => {
    const correctAnswers = attempt.correctAnswers || []
    const userAnswers = attempt.userAnswers || []
    correctAnswers.forEach((correct, index) => {
      if (!correct) return
      const userAnswer = userAnswers[index]
      const validation = revalidateStoredMarkerAnswer(userAnswer, correct, {
        index,
        progressionMode: attempt.progressionMode,
        tonality: attempt.tonality,
        mode: attempt.mode || attempt.dominantMode
      })
      const isCorrect = validation.level === 1
      const score = validation.score ?? 0

      const degree = (correct.degree || '').toUpperCase().replace('°', '')
      if (degree && DEGREE_TO_FUNCTIONS[degree]) {
        const func = DEGREE_TO_FUNCTIONS[degree][0]
        if (byFunction[func]) {
          byFunction[func].total++
          if (isCorrect) byFunction[func].correct++
        }
        byFunction.total++
      }

      const cadence = (correct.cadence || '').trim() || 'sans'
      if (!byCadence[cadence]) byCadence[cadence] = { correct: 0, total: 0, totalScore: 0 }
      byCadence[cadence].total++
      byCadence[cadence].totalScore += score
      if (isCorrect) byCadence[cadence].correct++

      if (degree) {
        if (!byDegree[degree]) byDegree[degree] = { correct: 0, total: 0, totalScore: 0 }
        byDegree[degree].total++
        byDegree[degree].totalScore += score
        if (isCorrect) byDegree[degree].correct++
      }

      const fig = (correct.figure || '5').toString().trim()
      const isFundamental = fig === '5' || fig === ''
      if (isFundamental) {
        byFigure.fundamental.total++
        if (isCorrect) byFigure.fundamental.correct++
      } else {
        byFigure.renversement.total++
        if (isCorrect) byFigure.renversement.correct++
      }
      byFigure.total++
    })
  })

  const cadenceEntries = Object.entries(byCadence)
    .filter(([k, v]) => v.total > 0 && k !== 'sans' && k !== '')
    .map(([name, v]) => ({
      name: CADENCE_SHORT_LABELS[name] || name,
      pct: Math.round((v.totalScore ?? 0) / v.total),
      total: v.total
    }))
    .sort((a, b) => b.pct - a.pct)

  const degreeEntries = Object.entries(byDegree)
    .filter(([, v]) => v.total > 0)
    .map(([degree, v]) => ({
      degree,
      pct: Math.round((v.totalScore ?? 0) / v.total),
      total: v.total
    }))
    .sort((a, b) => b.pct - a.pct)

  return {
    byFunction: {
      ...byFunction,
      T:
        byFunction.T.total > 0
          ? { ...byFunction.T, pct: Math.round((byFunction.T.correct / byFunction.T.total) * 100) }
          : null,
      SD:
        byFunction.SD.total > 0
          ? { ...byFunction.SD, pct: Math.round((byFunction.SD.correct / byFunction.SD.total) * 100) }
          : null,
      D:
        byFunction.D.total > 0
          ? { ...byFunction.D, pct: Math.round((byFunction.D.correct / byFunction.D.total) * 100) }
          : null
    },
    byCadence: {
      total: Object.values(byCadence).reduce((s, v) => s + v.total, 0),
      best: cadenceEntries[0]
        ? { name: cadenceEntries[0].name, pct: cadenceEntries[0].pct }
        : null,
      weak:
        cadenceEntries.length > 1
          ? {
              name: cadenceEntries[cadenceEntries.length - 1].name,
              pct: cadenceEntries[cadenceEntries.length - 1].pct
            }
          : null
    },
    byDegree: {
      total: Object.values(byDegree).reduce((s, v) => s + v.total, 0),
      strong: degreeEntries[0]
        ? { degree: degreeEntries[0].degree, pct: degreeEntries[0].pct }
        : null,
      weak:
        degreeEntries.length > 1
          ? {
              degree: degreeEntries[degreeEntries.length - 1].degree,
              pct: degreeEntries[degreeEntries.length - 1].pct
            }
          : degreeEntries[0]
            ? { degree: degreeEntries[0].degree, pct: degreeEntries[0].pct }
            : null
    },
    byFigure: {
      total: byFigure.total,
      fundamental:
        byFigure.fundamental.total > 0
          ? {
              pct: Math.round(
                (byFigure.fundamental.correct / byFigure.fundamental.total) * 100
              )
            }
          : null,
      renversement:
        byFigure.renversement.total > 0
          ? {
              pct: Math.round(
                (byFigure.renversement.correct / byFigure.renversement.total) * 100
              )
            }
          : null
    }
  }
}

/**
 * Activité hebdomadaire compacte (remplace WeeklyStats + PeriodComparison KPI).
 */
export function getWeeklyActivitySnapshot(attempts) {
  const thisWeek = getThisWeekAttempts(attempts)
  const lastWeek = getLastWeekAttempts(attempts)

  const avgRecords = (list) => {
    const best = getBestAttemptsByExercise(list)
    return best.length > 0 ? best.reduce((sum, a) => sum + (a.score || 0), 0) / best.length : 0
  }

  const thisUniqueCount = countUniqueExercises(thisWeek)
  const lastUniqueCount = countUniqueExercises(lastWeek)
  const thisSessionCount = thisWeek.length
  const lastSessionCount = lastWeek.length
  const thisAvg = avgRecords(thisWeek)
  const lastAvg = avgRecords(lastWeek)
  const thisBestAttempts = getBestAttemptsByExercise(thisWeek)
  const bestScore =
    thisBestAttempts.length > 0 ? Math.max(...thisBestAttempts.map((a) => a.score || 0)) : null

  const activeDays = new Set()
  thisWeek.forEach((a) => {
    const date = attemptDate(a)
    if (!date) return
    activeDays.add(
      `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
    )
  })

  const countChange = calculateChange(thisUniqueCount, lastUniqueCount)
  const scoreChange =
    lastWeek.length > 0 ? calculateChange(thisAvg, lastAvg) : { direction: 'stable', percentage: 0 }

  return {
    /** @deprecated préférer uniqueCount — conservé pour compat */
    count: thisUniqueCount,
    uniqueCount: thisUniqueCount,
    sessionCount: thisSessionCount,
    avgScore: thisWeek.length > 0 ? Math.round(thisAvg) : null,
    bestScore: bestScore != null ? Math.round(bestScore) : null,
    activeDays: activeDays.size,
    lastWeekCount: lastUniqueCount,
    lastWeekSessionCount: lastSessionCount,
    lastWeekAvgScore: lastWeek.length > 0 ? Math.round(lastAvg) : null,
    deltaCountPct: countChange.percentage,
    deltaCountDirection: countChange.direction,
    deltaScorePct: scoreChange.percentage,
    deltaScoreDirection: scoreChange.direction,
    hasLastWeek: lastWeek.length > 0
  }
}

/**
 * Retire les exemples entre parenthèses (ex. III pour V) d’un libellé diagnostic.
 */
export function stripDetailExamples(detail) {
  if (!detail) return null
  const cleaned = detail
    .replace(/\s*\(ex\.[^)]*\)/gi, '')
    .replace(/\s+/g, ' ')
    .trim()
  return cleaned || null
}

/**
 * Formate un point actionnable pour une ligne du bilan (priorités 1–3).
 */
export function mapPointToBilanPriority(point, index = 0) {
  if (!point) return null
  const practiceName =
    point.practiceLabel || formatPracticeLabel(point.filterTag, point) || point.name
  const pct = point.percentage ?? point.successRate
  const isDegree = point.isDegree || point.type === 'degree'

  let title = point.label || point.name
  if (isDegree && point.type !== 'pattern') {
    title = point.name || point.label
  }

  let subtitle = null
  let detailWhy = null
  if (point.type === 'pattern' && point.detail) {
    detailWhy = stripDetailExamples(point.detail) || point.detail
  } else if (point.detail) {
    subtitle = point.detail
  }
  if (
    !subtitle &&
    !detailWhy &&
    point.type === 'degree' &&
    point.filterTag &&
    point.filterTag !== getFilterTagForWeakSkill(point)
  ) {
    subtitle = `${point.name} relève de ${practiceName} — entraîne cette famille.`
  }

  let meta = null
  if (point.evidence?.n && point.type === 'pattern') {
    meta = `${point.evidence.n} confusions récentes`
  } else if (pct != null && point.type !== 'pattern') {
    meta = `${Math.round(pct)} % de réussite`
  }

  return {
    rank: index + 1,
    title,
    subtitle,
    detailWhy,
    meta,
    ctaLabel: `Travailler ${practiceName}`,
    point,
    codex: point.codex || getCodexForWeakPoint(point)
  }
}

/** Sous-titre de la section exercices recommandés (Suivi). */
export function formatSuggestionsSubtitle(point) {
  if (!point) return 'Selon ta progression récente.'
  if (point.suggestionsSubtitle) return point.suggestionsSubtitle
  const practiceName =
    point.practiceLabel || formatPracticeLabel(point.filterTag, point) || point.name
  if (point.type === 'pattern') {
    const label = (point.label || point.name || '').trim()
    let lc = label ? label.charAt(0).toLowerCase() + label.slice(1) : practiceName
    if (lc && !/^(les |la |le |l'|des )/.test(lc)) {
      lc = `les ${lc}`
    }
    return `Pour t'entraîner sur ${lc}.`
  }
  if (
    point.type === 'degree' &&
    point.filterTag &&
    point.filterTag !== getFilterTagForWeakSkill(point)
  ) {
    return `Pour consolider ${practiceName}.`
  }
  return `Pour travailler ${practiceName}.`
}

/**
 * Copy factuelle pour le hero diagnostic (sans encouragements cosmétiques).
 * `priorities` : jusqu’à 3 points actionnables → variant liste compacte.
 */
export function buildDiagnosticCopy({
  strongest,
  weakest,
  priorities = [],
  pedagogical,
  activeNode,
  hasHomework = false,
  homeworkCount = 0
} = {}) {
  if (hasHomework && homeworkCount > 0) {
    return {
      variant: 'simple',
      headline:
        homeworkCount === 1
          ? 'Tu as 1 devoir à faire.'
          : `Tu as ${homeworkCount} devoirs à faire.`,
      detail: 'Priorité : terminer les consignes de ton professeur.',
      primaryCta: 'Voir les devoirs',
      secondaryCta: activeNode?.label ? 'Continuer le parcours' : 'Voir les exercices recommandés',
      primaryAction: 'homework',
      secondaryAction: activeNode?.id ? 'parcours' : 'suggestions'
    }
  }

  const priorityList = (priorities || [])
    .slice(0, 3)
    .map((p, i) => mapPointToBilanPriority(p, i))
    .filter(Boolean)

  if (priorityList.length > 0) {
    return {
      variant: 'priorities',
      sectionTitle: 'Priorités cette semaine',
      priorities: priorityList,
      secondaryCta: activeNode?.id ? 'Continuer le parcours' : 'Voir les exercices recommandés',
      secondaryAction: activeNode?.id ? 'parcours' : 'suggestions'
    }
  }

  if (weakest) {
    const practiceName =
      weakest.practiceLabel || formatPracticeLabel(weakest.filterTag, weakest) || weakest.name
    const remapped =
      weakest.filterTag &&
      weakest.type === 'degree' &&
      weakest.filterTag !== getFilterTagForWeakSkill(weakest)

    let detail = weakest.detail || null
    if (!detail && remapped) {
      detail = `${weakest.name} relève de ${practiceName} — entraîne cette famille.`
    } else if (!detail && activeNode?.label) {
      detail = `Priorité : travailler ce point, ou continuer le nœud « ${activeNode.label} ».`
    } else if (!detail) {
      detail = 'Priorité : 2 exercices ciblés sur ce point.'
    }

    const pct = weakest.percentage ?? weakest.successRate
    const headline =
      weakest.type === 'pattern' || pct == null
        ? `Point faible : ${weakest.label || weakest.name}.`
        : `Point faible : ${weakest.name} (${pct} %).`

    return {
      variant: 'simple',
      headline,
      detail,
      primaryCta: `Travailler ${practiceName}`,
      secondaryCta: activeNode?.id ? 'Continuer le parcours' : 'Voir les exercices recommandés',
      primaryAction: 'weak_skill',
      secondaryAction: activeNode?.id ? 'parcours' : 'suggestions'
    }
  }

  if (activeNode?.label) {
    return {
      variant: 'simple',
      headline: `Nœud en cours : ${activeNode.label}.`,
      detail: activeNode.modeLabel
        ? `Mode ${activeNode.modeLabel}${activeNode.masteryLabel ? ` — maîtrise ${activeNode.masteryLabel}` : ''}.`
        : 'Continue le parcours pour progresser étape par étape.',
      primaryCta: 'Continuer le parcours',
      secondaryCta: 'Voir les exercices recommandés',
      primaryAction: 'parcours',
      secondaryAction: 'suggestions'
    }
  }

  if (strongest) {
    return {
      variant: 'simple',
      headline: `Point fort : ${strongest.name} (${strongest.percentage} %).`,
      detail: 'Pas encore assez de contraste pour un point faible. Continue à pratiquer.',
      primaryCta: 'Voir les exercices recommandés',
      secondaryCta: 'Explorer le parcours',
      primaryAction: 'suggestions',
      secondaryAction: 'parcours'
    }
  }

  const hasPedago =
    pedagogical &&
    (pedagogical.byFunction?.total > 0 ||
      pedagogical.byCadence?.total > 0 ||
      pedagogical.byDegree?.total > 0)

  if (hasPedago) {
    return {
      variant: 'simple',
      headline: 'Bilan en cours de construction.',
      detail: 'Encore quelques exercices pour identifier clairement un point faible.',
      primaryCta: 'Voir les exercices recommandés',
      secondaryCta: 'Continuer le parcours',
      primaryAction: 'suggestions',
      secondaryAction: 'parcours'
    }
  }

  return {
    variant: 'simple',
    headline: 'Pas encore assez de données.',
    detail: 'Fais 1–2 exercices pour obtenir un bilan et un plan de travail.',
    primaryCta: 'Commencer une séance',
    secondaryCta: 'Voir le parcours',
    primaryAction: 'home',
    secondaryAction: 'parcours'
  }
}

export function getCodexForWeakPoint(weakest) {
  if (!weakest) return null
  if (weakest.codex) return weakest.codex
  const key = weakest.filterTag || weakest.value || weakest.name
  return WEAK_SKILL_CODEX[key] || null
}

/** True si un tag d'exercice correspond au filterTag d'un point faible (insensible à la casse). */
export function exerciseTagListMatchesFilter(exerciseTags, filterTag) {
  if (!filterTag || !Array.isArray(exerciseTags) || exerciseTags.length === 0) return false
  const target = String(filterTag).toLowerCase()
  return exerciseTags.some((tag) => String(tag).toLowerCase() === target)
}

/** Un exercice cible-t-il ce point faible via ses autoTags ? */
export function exerciseMatchesWeakPoint(exercise, weakPoint) {
  if (!exercise || !weakPoint) return false
  const tags = exercise.autoTags || []
  if (weakPoint.filterTag && exerciseTagListMatchesFilter(tags, weakPoint.filterTag)) {
    return true
  }
  if (weakPoint.type === 'degree' && weakPoint.value) {
    const v = String(weakPoint.value).toLowerCase()
    return tags.some((tag) => {
      const t = String(tag).toLowerCase()
      return t === `degré${v}` || t === `degre${v}` || t.includes(v)
    })
  }
  if (weakPoint.type === 'cadence' && weakPoint.value) {
    const v = String(weakPoint.value).toLowerCase()
    return tags.some((tag) => {
      const t = String(tag).toLowerCase()
      return t.includes(v) || (v === 'half' && t.includes('demicadence'))
    })
  }
  return false
}

const DIFFICULTY_LEVELS = { facile: 1, intermédiaire: 2, difficile: 3, expert: 4 }

function normalizeDifficultyLevel(difficulty) {
  const raw = String(difficulty || 'intermédiaire')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
  if (raw.includes('facile') || raw === 'easy') return 1
  if (raw.includes('difficile') || raw === 'hard') return 3
  if (raw.includes('expert')) return 4
  return 2
}

/**
 * Score de pertinence d'un exercice pour des points faibles (match tag requis).
 * primaryIndex : pondération double sur le point faible #1.
 */
export function scoreExerciseForWeakPoints(
  exercise,
  weakPoints,
  stats,
  attempts,
  { primaryIndex = 0 } = {}
) {
  if (!exercise || !weakPoints?.length) return 0

  let matchScore = 0
  weakPoints.forEach((weakPoint, index) => {
    if (!exerciseMatchesWeakPoint(exercise, weakPoint)) return
    const weight = index === primaryIndex ? 2 : 1
    const rate = weakPoint.successRate ?? weakPoint.percentage ?? 50
    matchScore += (100 - rate) * weight
  })
  if (matchScore <= 0) return 0

  let tieBreak = 0
  const attemptsList = attempts || []
  if (!attemptsList.some((a) => a.exerciseId === exercise.id)) tieBreak += 20

  const avgScore = stats?.averageScore ?? 50
  const exerciseDifficulty =
    DIFFICULTY_LEVELS[exercise.metadata?.difficulty] ??
    normalizeDifficultyLevel(exercise.metadata?.difficulty)
  if (avgScore < 60 && exerciseDifficulty <= 2) tieBreak += 15
  else if (avgScore >= 80 && exerciseDifficulty >= 3) tieBreak += 15

  return matchScore + tieBreak
}

function cadenceValuesMatch(stored, target) {
  const c = String(stored || '').toLowerCase()
  const v = String(target || '').toLowerCase()
  if (!c || !v) return false
  if (c === v) return true
  if ((v === 'half' || v === 'demi-cadence') && (c === 'half' || c === 'demi-cadence')) return true
  return false
}

/** Une réponse cible-t-elle ce point faible (degré, cadence ou filterTag) ? */
export function answerMatchesWeakPoint(correctAnswer, weakPoint) {
  if (!correctAnswer || !weakPoint) return false
  if (weakPoint.type === 'degree') {
    return (correctAnswer.degree || '') === weakPoint.value
  }
  if (weakPoint.type === 'cadence') {
    return cadenceValuesMatch(correctAnswer.cadence, weakPoint.value)
  }
  const filterTag = weakPoint.filterTag
  if (!filterTag) return false
  const deg = String(correctAnswer.degree || '')
    .trim()
    .toUpperCase()
  if (deg && DEGREE_TO_FUNCTION_FILTER_TAG[deg] === filterTag) return true
  if (deg && `Degré${deg}` === filterTag) return true
  const cadenceTag = CADENCE_TO_FILTER_TAG[String(correctAnswer.cadence || '').trim().toLowerCase()]
  return cadenceTag === filterTag
}

/** Compte les succès ciblés (≥ minScore) cette semaine pour un objectif compétence. */
export function countWeeklyTargetedSuccesses(attempts, weakPoint, { minScore = 70 } = {}) {
  if (!weakPoint) return 0
  const weekAttempts = getThisWeekAttempts(attempts || [])
  let successes = 0

  weekAttempts.forEach((attempt) => {
    if ((attempt.score ?? 0) < minScore) return

    if (
      weakPoint.filterTag &&
      Array.isArray(attempt.autoTags) &&
      exerciseTagListMatchesFilter(attempt.autoTags, weakPoint.filterTag)
    ) {
      successes++
      return
    }

    let matchedAnswers = 0
    ;(attempt.correctAnswers || []).forEach((correct, i) => {
      if (!correct || !attempt.userAnswers?.[i]) return
      if (answerMatchesWeakPoint(correct, weakPoint)) matchedAnswers++
    })
    if (matchedAnswers > 0) successes += matchedAnswers
  })

  return successes
}

/**
 * Tag Mode Libre « primaire » pour un point faible (degré / cadence).
 * Ex. VII → DegréVII, perfect → CadenceParfaite.
 * Ne lit pas `point.filterTag` (peut être un fallback fonction déjà résolu).
 */
export function getFilterTagForWeakSkill(point) {
  if (!point) return null

  const isDegree = point.type === 'degree' || point.isDegree === true
  if (isDegree) {
    const raw = String(point.value || point.name || '')
      .trim()
      .toUpperCase()
      .replace(/°/g, '')
    if (/^[IVX]+$/.test(raw)) return `Degré${raw}`
    return null
  }

  const cadenceKey = String(point.value || '')
    .trim()
    .toLowerCase()
  if (CADENCE_TO_FILTER_TAG[cadenceKey]) return CADENCE_TO_FILTER_TAG[cadenceKey]

  const name = String(point.name || point.label || '').toLowerCase()
  if (name.includes('rompue')) return 'CadenceRompue'
  if (name.includes('plagale')) return 'CadencePlagale'
  if (name.includes('imparfaite')) return 'CadenceImparfaite'
  if (name.includes('parfaite')) return 'CadenceParfaite'
  if (name.includes('demi')) return 'DemiCadence'
  return null
}

function toTagSet(availableTags) {
  if (availableTags instanceof Set) return availableTags
  return new Set(Array.isArray(availableTags) ? availableTags : [])
}

/** Tags autoTags présents sur un catalogue d’exercices. */
export function collectAutoTagsFromExercises(exercises) {
  const set = new Set()
  ;(exercises || []).forEach((ex) => {
    ;(ex.autoTags || []).forEach((tag) => {
      if (tag && typeof tag === 'string') set.add(tag)
    })
  })
  return set
}

function tagSetHas(tagSet, tag) {
  if (!tag || !tagSet || tagSet.size === 0) return false
  if (tagSet.has(tag)) return true
  const lower = tag.toLowerCase()
  for (const t of tagSet) {
    if (String(t).toLowerCase() === lower) return true
  }
  return false
}

export function formatPracticeLabel(filterTag, point) {
  if (!filterTag) return point?.name || point?.label || ''
  if (filterTag.startsWith('Degré')) return filterTag.replace(/^Degré/, '')
  if (filterTag === 'Tonique') return 'la tonique'
  if (filterTag === 'SousDominante') return 'la sous-dominante'
  if (filterTag === 'Dominante') return 'la dominante'
  if (filterTag === 'PremierRenversement') return 'les 1ers renversements'
  if (filterTag === 'SecondRenversement') return 'les 2e renversements'
  if (filterTag === 'CadenceParfaite') return 'la cadence parfaite'
  if (filterTag === 'CadenceImparfaite') return 'la cadence imparfaite'
  if (filterTag === 'CadencePlagale') return 'la cadence plagale'
  if (filterTag === 'CadenceRompue') return 'la cadence rompue'
  if (filterTag === 'DemiCadence') return 'la demi-cadence'
  return filterTag
}

const RARE_DEGREES = new Set(['VII', 'III'])

function minAttemptsForPoint(point, defaultMin = 3) {
  if (point?.type === 'pattern') return 3
  const isDegree = point?.type === 'degree' || point?.isDegree === true
  if (!isDegree) return defaultMin
  const deg = String(point.value || point.name || '')
    .trim()
    .toUpperCase()
    .replace(/°/g, '')
  return RARE_DEGREES.has(deg) ? 5 : defaultMin
}

/**
 * Pool unifié de points faibles actionnables (motifs + degrés/cadences), max N.
 * Priorité : motif > cadence > degré ; stats sur les dernières tentatives.
 */
export function getActionableWeakPoints(
  profileStats,
  attempts,
  availableTags,
  { max = 3, threshold = 70, minAttempts = 3, recentLimit = 50 } = {}
) {
  const tagSet = toTagSet(availableTags)
  if (tagSet.size === 0) return []

  const recentAttempts = getRecentAttempts(attempts || [], recentLimit)
  const recentBestAttempts = getBestAttemptsByExercise(recentAttempts)
  const recentStats =
    recentBestAttempts.length > 0 ? buildDegreeAndCadenceStats(recentBestAttempts) : profileStats

  const patterns = detectErrorPatterns(recentAttempts, { limit: recentLimit })
  const patternPoints = patternsToWeakPoints(patterns)

  const skills = getWeakSkills(recentStats || profileStats || {}, {
    threshold,
    minAttempts,
    availableTags: null
  })

  const skillPoints = skills
    .filter((s) => {
      const stats =
        s.type === 'degree'
          ? (profileStats?.degreeStats || {})[s.value]
          : (profileStats?.cadenceStats || {})[s.value]
      const total = stats?.total ?? 0
      return total >= minAttemptsForPoint(s, minAttempts)
    })
    .map((s) => {
      const stats =
        s.type === 'degree'
          ? (recentStats || profileStats)?.degreeStats?.[s.value]
          : (recentStats || profileStats)?.cadenceStats?.[s.value]
      return {
        ...s,
        name: s.label,
        percentage: s.successRate,
        isDegree: s.type === 'degree',
        priority: s.type === 'cadence' ? 2 : 3,
        evidence: { n: stats?.total ?? 0 }
      }
    })

  let pool = [...patternPoints, ...skillPoints]
    .map((p) => {
      const filterTag =
        p.filterTag && tagSetHas(tagSet, p.filterTag)
          ? p.filterTag
          : resolveActionableFilterTag(p, tagSet)
      if (!filterTag) return null
      return {
        ...p,
        filterTag,
        practiceLabel: p.practiceLabel || formatPracticeLabel(filterTag, p)
      }
    })
    .filter(Boolean)

  const hasBassOnly = pool.some((p) => p.value === 'bass_only')
  if (hasBassOnly) {
    pool = pool.filter((p) => {
      if (p.value === 'degree_ok_figure_ko') return false
      if (p.type !== 'pattern' && p.filterTag === 'PremierRenversement') return false
      return true
    })
  }

  const byTag = new Map()
  pool.forEach((p) => {
    const key = p.filterTag
    const existing = byTag.get(key)
    const rate = p.successRate ?? p.percentage ?? 0
    const existingRate = existing?.successRate ?? existing?.percentage ?? 0
    if (
      !existing ||
      (p.priority ?? 3) < (existing.priority ?? 3) ||
      ((p.priority ?? 3) === (existing.priority ?? 3) && rate < existingRate)
    ) {
      byTag.set(key, p)
    }
  })

  return [...byTag.values()]
    .sort((a, b) => {
      const pa = a.priority ?? 3
      const pb = b.priority ?? 3
      if (pa !== pb) return pa - pb
      return (a.successRate ?? a.percentage ?? 0) - (b.successRate ?? b.percentage ?? 0)
    })
    .slice(0, max)
}

/** Point faible hero (1 seul) — exclut les signaux trop faibles pour degrés rares. */
export function getHeroWeakPoint(actionablePoints) {
  const hero = actionablePoints?.[0]
  if (!hero) return null
  if (hero.type === 'pattern') return hero
  const n = hero.evidence?.n ?? 0
  const required = minAttemptsForPoint(hero)
  return n >= required ? hero : null
}

/**
 * Tag d’exercice réellement présent dans le catalogue pour travailler ce point.
 * Ordre : tag degré/cadence exact, puis (pour un degré) tag de fonction Riemann.
 */
export function resolveActionableFilterTag(point, availableTags) {
  const tagSet = toTagSet(availableTags)
  if (!point || tagSet.size === 0) return null

  if (point.filterTag && tagSetHas(tagSet, point.filterTag)) return point.filterTag

  const primary = getFilterTagForWeakSkill(point)
  if (primary && tagSetHas(tagSet, primary)) return primary

  const isDegree = point.type === 'degree' || point.isDegree === true
  if (isDegree) {
    const deg = String(point.value || point.name || '')
      .trim()
      .toUpperCase()
      .replace(/°/g, '')
    const fnTag = DEGREE_TO_FUNCTION_FILTER_TAG[deg]
    if (fnTag && tagSetHas(tagSet, fnTag)) return fnTag
  }

  return null
}

/** True si le point faible peut ouvrir un filtre Mode Libre non vide. */
export function isWeakSkillActionable(point, availableTags) {
  return Boolean(resolveActionableFilterTag(point, availableTags))
}
